import { useState, useEffect } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useTaxYear } from "../contexts/TaxYearContext";
import axios from "axios";
import {
  Check, Star, Loader2, AlertTriangle, Lock, ArrowLeft,
  Cloud, Tag, BarChart2, FolderOpen, ShieldCheck, Mail,
  Home, Building2, TrendingUp, Sparkles, Calendar, Archive,
  Users, FileText, Settings, FilePlus
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const TIERS = [
  {
    id: "basic",
    label: "Basic",
    price: 99,
    pages: 100,
    icon: Home,
    tagline: "Perfect for smaller centers",
    color: "blue",
    btnClass: "bg-blue-600 hover:bg-blue-700 text-white",
    iconBg: "bg-blue-100 text-blue-600",
    cardBorder: "border-slate-200",
    priceColor: "text-slate-900",
    capLabel: "text-blue-600",
    capBox: "bg-blue-50",
    capIcon: "text-blue-500",
    titleColor: "text-slate-900",
  },
  {
    id: "premium",
    label: "Premium",
    price: 149,
    pages: 350,
    icon: TrendingUp,
    tagline: "Best for growing centers",
    popular: true,
    color: "purple",
    btnClass: "bg-purple-600 hover:bg-purple-700 text-white",
    iconBg: "bg-purple-100 text-purple-600",
    cardBorder: "border-purple-400 ring-2 ring-purple-200",
    priceColor: "text-slate-900",
    capLabel: "text-purple-600",
    capBox: "bg-purple-50",
    capIcon: "text-purple-500",
    titleColor: "text-purple-700",
  },
  {
    id: "premium_plus",
    label: "Premium Plus",
    price: 249,
    pages: 1000,
    icon: Building2,
    tagline: "Built for high-volume centers",
    color: "green",
    btnClass: "bg-emerald-600 hover:bg-emerald-700 text-white",
    iconBg: "bg-emerald-100 text-emerald-600",
    cardBorder: "border-emerald-300",
    priceColor: "text-slate-900",
    capLabel: "text-emerald-600",
    capBox: "bg-emerald-50",
    capIcon: "text-emerald-500",
    titleColor: "text-emerald-700",
  },
];

const TOP_FEATURES = [
  { icon: Cloud, label: "Smart upload\n+ AI OCR (Gemini)" },
  { icon: Tag, label: "Auto-categorization\n(36 IRS Schedule C categories)" },
  { icon: BarChart2, label: "All tax reports\n& PDF exports" },
  { icon: FolderOpen, label: "Archive by\ntax year" },
  { icon: ShieldCheck, label: "Secure & private\nencrypted" },
  { icon: Mail, label: "Email support" },
];

const FEATURE_COLUMNS = [
  [
    { text: "Smart upload + AI OCR (Gemini)" },
    { text: "Auto-categorization (36 IRS Schedule C categories)" },
    { text: "Manual transactions" },
    { text: "Schedule C PDF" },
    { text: "Branded PDF reports" },
    { text: "Equipment & Assets tracking" },
  ],
  [
    { text: "Form 4562 Part I PDF (equipment depreciation)" },
    { text: "Form 4562 Part V PDF (vehicle/listed property)", badge: "NEW" },
    { text: "Section 179 election helper" },
    { text: "CACFP simple summary" },
    { text: "Time-Space Percentage Calculator" },
  ],
  [
    { text: "Prohibited Cost Validator (USDA FNS 796-2 compliance)" },
    { text: "Food & Labor Synthetic Report (audit-ready)" },
    { text: "Center vs Home dual calculation" },
    { text: "Federal/State Tax Credit Hunter" },
    { text: "Grant Tracking" },
  ],
  [
    { text: "Year-End Wizard + Archive (locked years)" },
    { text: "Email Tax Prep (send package to accountant)" },
    { text: "Vehicle depreciation: actual-expenses method" },
  ],
];

const TRUST_ITEMS = [
  { icon: ShieldCheck, title: "Secure & Private", copy: "Your data is encrypted and never shared." },
  { icon: Calendar, title: "One Year of Access", copy: "Process, report, and archive for the full tax year." },
  { icon: Archive, title: "Archive & Lock", copy: "Lock your year when ready. Keep everything organized." },
  { icon: Users, title: "Works for All Centers", copy: "Family child care, home daycare, and center-based programs." },
];

const ADDONS = [
  { pages: "50 Pages", price: "$14.99" },
  { pages: "250 Pages", price: "$49.99", badge: "BEST VALUE" },
  { pages: "1,000 Pages", price: "$129.00" },
];

export default function Pricing() {
  const { user } = useAuth();
  const { currentCalendarYear } = useTaxYear();
  const navigate = useNavigate();
  const location = useLocation();
  const [busyTier, setBusyTier] = useState(null);
  const [error, setError] = useState("");

  const trialReason = location.state?.reason === "trial_expired";
  const lockedEmail = location.state?.email;

  const currentTier = user?.plan_tier || user?.license?.plan_tier;
  const isPaid = user?.license?.status === "paid" || user?.license?.type === "paid";
  const isOwner = user?.role === "owner" || user?.license?.type === "signature";

  useEffect(() => { setError(""); }, [busyTier]);

  const handleSubscribe = async (tier) => {
    setError("");
    setBusyTier(tier.id);
    try {
      const { data } = await axios.get(`${API}/subscriptions/checkout-url`, {
        params: { tier: tier.id, tax_year: currentCalendarYear },
        withCredentials: true,
      });
      if (data?.checkout_url) {
        window.location.href = data.checkout_url;
      } else {
        setError("Could not start checkout — please try again.");
        setBusyTier(null);
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : (detail?.message || "Checkout failed."));
      setBusyTier(null);
    }
  };

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: "IBM Plex Sans, sans-serif" }}>

      {/* ── TOP BANNER ── */}
      <div className="flex justify-center pt-8 px-4">
        <div className="inline-flex items-center gap-2 bg-purple-100 text-purple-700 rounded-full px-5 py-2">
          <Star className="w-3.5 h-3.5 fill-current text-purple-700" />
          <p className="text-xs font-bold tracking-widest uppercase">
            ONE POWERFUL PLATFORM. DIFFERENT SCALE LEVELS.
          </p>
        </div>
      </div>

      {/* ── HEADER ── */}
      <div className="max-w-6xl mx-auto px-4 pt-8 pb-8 text-center">
        {user && !trialReason && (
          <div className="text-left mb-4">
            <Link to="/app/settings" data-testid="pricing-back-link"
              className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800">
              <ArrowLeft className="w-4 h-4" /> Back to Settings
            </Link>
          </div>
        )}

        <h1 className="text-5xl sm:text-6xl lg:text-7xl font-extrabold text-slate-900 mb-6 leading-[1.05] tracking-tight"
          style={{ fontFamily: "Manrope" }}
          data-testid="pricing-page-title">
          Choose the Plan That Fits Your Center
        </h1>
        <p className="text-slate-600 text-lg mb-2">
          Every plan includes the same powerful tools and features.
        </p>
        <p className="text-blue-600 font-semibold text-lg mb-10">
          The only difference is how many pages you can process each year.
        </p>

        {/* Feature icon strip */}
        <div className="bg-white border border-slate-200 rounded-2xl shadow-sm px-8 py-5 mb-2">
          <div className="flex flex-wrap justify-center items-center gap-x-10 gap-y-3">
            {TOP_FEATURES.map((f, i) => (
              <div key={i} className="flex items-center gap-2.5 text-slate-600">
                <f.icon className="w-5 h-5 text-blue-500 flex-shrink-0" />
                <span className="text-xs font-medium whitespace-pre-line text-left leading-tight">{f.label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── TRIAL EXPIRED BANNER ── */}
      {trialReason && (
        <div className="max-w-6xl mx-auto px-4 mb-6">
          <div data-testid="trial-expired-banner"
            className="bg-amber-50 border-2 border-amber-300 rounded-xl p-5 flex items-start gap-4">
            <AlertTriangle className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-bold text-amber-900" style={{ fontFamily: "Manrope" }}>Your free trial has ended</p>
              <p className="text-amber-800 text-sm mt-1">
                {lockedEmail ? <>The account <span className="font-semibold">{lockedEmail}</span> is locked.</> : "Your account is locked."}
                {" "}Pick a plan below to unlock everything immediately.
              </p>
            </div>
          </div>
        </div>
      )}

      {error && (
        <div className="max-w-6xl mx-auto px-4 mb-4">
          <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-4 text-sm">{error}</div>
        </div>
      )}

      {isPaid && currentTier && !isOwner && (
        <div className="max-w-6xl mx-auto px-4 mb-4 text-center">
          <span className="inline-flex items-center gap-2 bg-blue-50 border border-blue-200 text-blue-700 rounded-full px-4 py-1.5 text-sm font-semibold"
            data-testid="current-plan-pill">
            <Sparkles className="w-3.5 h-3.5" />
            Your Tax Year {currentCalendarYear} Access: {TIERS.find(t => t.id === currentTier)?.label || currentTier}
          </span>
        </div>
      )}

      {/* ── PRICING CARDS ── */}
      <div className="max-w-6xl mx-auto px-4 pb-16">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-testid="pricing-cards">
          {TIERS.map((tier) => {
            const isCurrent = isPaid && currentTier === tier.id && !isOwner;
            const isLoading = busyTier === tier.id;
            const TierIcon = tier.icon;
            return (
              <div key={tier.id} data-testid={`pricing-card-${tier.id}`}
                className={`relative border-2 ${tier.cardBorder} rounded-3xl p-10 bg-white text-center ${tier.popular ? "shadow-2xl" : "shadow-md"}`}>
                {tier.popular && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-purple-600 text-white text-xs font-bold px-5 py-1.5 rounded-full flex items-center gap-1.5 whitespace-nowrap shadow-md">
                    <Star className="w-3.5 h-3.5 fill-current" /> MOST POPULAR
                  </div>
                )}

                <div className={`w-16 h-16 rounded-2xl ${tier.iconBg} flex items-center justify-center mb-6 mx-auto`}>
                  <TierIcon className="w-8 h-8" />
                </div>

                <h2 className={`text-3xl font-extrabold mb-2 ${tier.titleColor}`}
                  style={{ fontFamily: "Manrope" }}>
                  {tier.label}
                </h2>
                <p className="text-slate-500 text-base mb-7">{tier.tagline}</p>

                <div className="mb-8">
                  <div className={`text-5xl font-extrabold ${tier.priceColor}`} style={{ fontFamily: "Manrope" }}>
                    ${tier.price}
                    <span className="text-lg font-normal text-slate-400"> /tax year</span>
                  </div>
                </div>

                {/* Processing capacity */}
                <div className="mb-8">
                  <p className={`text-[11px] font-bold tracking-widest uppercase mb-3 ${tier.capLabel}`}>Processing Capacity</p>
                  <div className={`${tier.capBox} rounded-xl px-4 py-4 flex items-center justify-center gap-3`}>
                    <div className="w-10 h-10 rounded-lg bg-white flex items-center justify-center shadow-sm flex-shrink-0">
                      <FileText className={`w-5 h-5 ${tier.capIcon}`} />
                    </div>
                    <div className="text-left">
                      <p className="text-2xl font-extrabold text-slate-900 leading-none" style={{ fontFamily: "Manrope" }}>
                        {tier.pages.toLocaleString()} pages
                      </p>
                      <p className="text-xs text-slate-400 mt-1">per tax year</p>
                    </div>
                  </div>
                </div>

                {isCurrent ? (
                  <button disabled data-testid={`current-plan-${tier.id}`}
                    className="w-full py-4 rounded-xl text-base font-bold bg-slate-100 text-slate-400 cursor-not-allowed">
                    Current Plan
                  </button>
                ) : (
                  <button
                    onClick={() => handleSubscribe(tier)}
                    disabled={!!busyTier}
                    data-testid={`subscribe-${tier.id}`}
                    className={`w-full py-4 rounded-xl text-base font-bold transition-all flex items-center justify-center gap-2 ${tier.btnClass} disabled:opacity-60 shadow-sm hover:shadow-md`}
                  >
                    {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                    {isLoading ? "Loading..." : `Choose ${tier.label} – Tax Year ${currentCalendarYear}`}
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* ── ALL PLANS INCLUDE ── */}
      <div className="bg-slate-50 py-14">
        <div className="max-w-6xl mx-auto px-4">
          <h2 className="text-center text-xl font-extrabold text-slate-800 mb-8 tracking-wide uppercase"
            style={{ fontFamily: "Manrope" }}>
            All Plans Include the Full Platform
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {FEATURE_COLUMNS.map((col, ci) => (
              <ul key={ci} className="space-y-3">
                {col.map((f, fi) => (
                  <li key={fi} className="flex items-start gap-2 text-sm text-slate-700">
                    <Check className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />
                    <span className="leading-snug">
                      {f.text}
                      {f.badge && (
                        <span className="ml-1.5 inline-block bg-purple-600 text-white text-[9px] font-bold px-1.5 py-0.5 rounded uppercase tracking-wide">
                          {f.badge}
                        </span>
                      )}
                    </span>
                  </li>
                ))}
              </ul>
            ))}
          </div>
        </div>
      </div>

      {/* ── TRUST SECTION ── */}
      <div className="max-w-6xl mx-auto px-4 py-14">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          {TRUST_ITEMS.map((t, i) => (
            <div key={i} className="flex flex-col items-center text-center gap-2">
              <div className="w-12 h-12 rounded-2xl bg-blue-50 flex items-center justify-center">
                <t.icon className="w-6 h-6 text-blue-600" />
              </div>
              <p className="font-bold text-slate-800 text-sm" style={{ fontFamily: "Manrope" }}>{t.title}</p>
              <p className="text-slate-500 text-xs leading-snug">{t.copy}</p>
            </div>
          ))}
        </div>
      </div>

      {/* ── ADD-ON PACKS ── */}
      <div className="border-t border-slate-100 bg-white py-12">
        <div className="max-w-6xl mx-auto px-4">
          <div className="flex flex-col md:flex-row items-start md:items-center gap-8">
            <div className="flex items-start gap-3 flex-shrink-0">
              <div className="w-12 h-12 rounded-2xl bg-purple-50 flex items-center justify-center">
                <FilePlus className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <p className="font-bold text-slate-900" style={{ fontFamily: "Manrope" }}>Need More Pages?</p>
                <p className="text-slate-500 text-sm">Add-on page packs are available anytime.</p>
              </div>
            </div>
            <div className="flex flex-wrap gap-4">
              {ADDONS.map((a, i) => (
                <div key={i} className="text-center">
                  <p className="text-sm text-slate-500 mb-0.5">{a.pages}</p>
                  <p className="text-2xl font-extrabold text-slate-900" style={{ fontFamily: "Manrope" }}>{a.price}</p>
                  {a.badge && (
                    <span className="inline-block bg-emerald-500 text-white text-[9px] font-bold px-2 py-0.5 rounded uppercase tracking-wide mt-0.5">
                      {a.badge}
                    </span>
                  )}
                </div>
              ))}
            </div>
            <div className="flex items-center gap-2 text-sm text-slate-500 md:ml-auto">
              <Settings className="w-4 h-4 text-purple-500" />
              <span>Manage add-ons anytime in{" "}
                <Link to="/app/settings" className="text-purple-600 font-semibold hover:underline">
                  Settings → Billing
                </Link>
              </span>
            </div>
          </div>
        </div>
      </div>

    </div>
  );
}
